import { Component, Input, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { CoinReward, ProductReward, RewardChance } from 'src/app/api/api-campaigns.service.generated';
import { ProductInfo, RewardsProductApiClient } from 'src/app/api/api-rewards.service.generated';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@angular/flex-layout';
import { lastValueFrom } from 'rxjs';


@Component({
  selector: 'app-reward-chances-select',
  templateUrl: './reward-chances-select.component.html',
  styleUrls: ['./reward-chances-select.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FlexLayoutModule
  ]
})
export class RewardChancesSelectComponent implements OnInit {
  private fb = inject(FormBuilder);
  private rewardProductsApi = inject(RewardsProductApiClient);


  @Input() advertiserId: string;

  form = this.fb.group({
    rewardChances: this.fb.array([], [Validators.required, this.rewardValidator()])
  }
  );

  coinRewardOptions = [
    new CoinReward({ coins: 1 }),
    new CoinReward({ coins: 2 }),
    new CoinReward({ coins: 3 }),
    new CoinReward({ coins: 5 }),
    new CoinReward({ coins: 8 }),
    new CoinReward({ coins: 10 }),
    new CoinReward({ coins: 20 }),
    new CoinReward({ coins: 50 }),
    new CoinReward({ coins: 100 })
  ];
  rewardProductsOptions: ProductReward[] = [];


  async ngOnInit() {
    const productInfos = await lastValueFrom(this.rewardProductsApi.getProductsByAdvertiserId(this.advertiserId, false, false));
    this.rewardProductsOptions = productInfos.map((pi: ProductInfo) =>
      new ProductReward({
        productSlug: pi.slug,
        rewardTitle: pi.name,
        rewardDescription: pi.description,
        rewardThumbUrl: pi.photoUrl,
        rewardPhotoUrl: pi.photoUrl
      }));
  }



  setFormValue(rewardChances: RewardChance[]) {

    // add empty item for reward chance
    if (!rewardChances || rewardChances.length === 0) {
      rewardChances = [new RewardChance({})];
    }
    this.bindRewardChances(rewardChances);  // init reactive forms list of reward chances
  }

  getFormValue(): RewardChance[] {
    const f = this.form.value;

    const chances = f.rewardChances
      .map((rwc: any) => new RewardChance({
        chance: +rwc.chance,
        coinReward: rwc.coinReward != null ? new CoinReward({ coins: rwc.coinReward.coins }) : null,
        productReward: rwc.productReward != null ? new ProductReward({ ...rwc.productReward }) : null  // must be new object otherwise will throw this.productReward.toJSON is not a function
      }));

    return chances;
  }

  isValid() {
    const isValid = this.form.valid;
    if (!isValid)
      this.forceValidatorsToDisplayErrors();
    return isValid;
  }


  async onSubmit() {
  }



  // rewardChance reactive array
  addRewardChance(): FormGroup {

    const arr = <FormArray>this.form.controls.rewardChances;

    const grp = this.fb.group({
      chance: new FormControl('', [Validators.required, Validators.min(0), Validators.max(1)]),
      coinReward: new FormControl(''),
      productReward: new FormControl(''),
    });
    arr.push(grp);

    this.form.updateValueAndValidity();

    return grp;
  }
  onRemoveChance(index: number) {
    const rewardChances = this.getFormValue();
    rewardChances.splice(index, 1);
    this.bindRewardChances(rewardChances);
  }
  bindRewardChances(rewardChances: RewardChance[]) {
    const reactiveControls = <FormArray>this.form.controls.rewardChances;
    reactiveControls.controls.length = 0;
    reactiveControls.patchValue([]);

    for (let i = 0; i < rewardChances.length; i++) {
      const rwc = rewardChances[i];
      const grp = this.addRewardChance();
      grp.patchValue({ ...rwc });
    }
  }

  compareFn: ((a: any, b: any) => boolean) | null = (a: any, b: any) => a && b && a.id === b.id;
  compareRewardFn: ((a: CoinReward, b: CoinReward) => boolean) | null = (a: any, b: any) => a && b && a.coins === b.coins;
  compareProductRewardFn: ((a: ProductReward, b: ProductReward) => boolean) | null = (a: ProductReward, b: ProductReward) => a && b && a.productSlug === b.productSlug;



  forceValidatorsToDisplayErrors() {
    // force validators to display errors
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key).markAsTouched();
      this.form.get(key).markAsDirty();
    });

    // const arr = <FormArray>this.form.controls.rewardChances;
    // for (let chance of arr.controls) {
    //   chance.markAsDirty();
    //   chance.markAsTouched();
    // }
  }

  rewardValidator(): ValidatorFn {
    // custom validator to ensure user has selected a product reward OR a coin reward OR both
    return (form: any): ValidationErrors | null => {

      if (!form || !form.controls || !form.controls)
        return { rewardRequired: true };


      for (let i = 0; i < form.controls.length; i++) {
        const chance = form.controls[i] as FormGroup;
        const c1 = chance.get('coinReward');
        const c2 = chance.get('productReward');
        const invalid = !c1.value && !c2.value;
        if (invalid) {
          chance.setErrors({ rewardInvalid: true });
          return { rewardInvalid: true, index: (i + 1) };
        } else
          chance.setErrors(null);

        chance.markAsDirty();
        chance.markAsTouched();
      }

      return null;
    };
  }

}
